import React, { Suspense } from 'react'
import {
  Button,
  Card,
  CardContent,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import EditNote from '@mui/icons-material/EditNote'
import InvestigationForm from 'src/clinic/components/InvestigationsForm/InvestigationsForm'
import { ConfirmationModal } from 'src/consumer/components/helperComponents/dynamic'
import {
  HE_KEY_ADVICE,
  HE_KEY_DIAGNOSIS,
  HE_KEY_HEALTH_COMPLAINTS,
  HE_KEY_INSTRUCTIONS,
  HE_KEY_INVESTIGATION,
  HE_KEY_OBSERVATIONS,
} from 'src/consumer/constants'
import { convertAssessmentIdBasedCategoryListToMetaCategoryList } from 'src/consumer/helpers/utilFunctions'
import JointHomunculusComponent from 'src/emr/components/swollenJointHighlighter/SwollenJointHighlighter'
import EmrSectionDataHandler from 'src/mentor/components/EmrRelatedComponents/components/EmrSectionDataHandler'
import ScreeningRecommendationEmrSection from 'src/mentor/components/EmrRelatedComponents/screeningRecommendation/ScreeningRecommendationEmrSection'
import PatientHistoryNotesDisplay from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/PatientHistoryNotesDisplay'
import ScreeningReportViewer from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/ScreeningReportViewer'
import { StyledRTEContainer } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/styledComponents'
import PatientHistoryTipTapEditor from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/PatientHistoryTipTapEditor'
import AdviceInputComponent from '../inputComponents/advice/AdviceInput'
import HealthComplaintsInputComponent from '../inputComponents/complaints/HealthComplaintsInputComponent'
import DiagnosisSelectMenu from '../inputComponents/diagnosis/DiagnosisMenu'
import DrugAdviceInput from '../inputComponents/drugAdvice/DrugAdviceInput'
import EMRInstructionsInput from '../inputComponents/instructions/EMRInstructionsInput'
import { InvestigationsSelectMenu } from '../inputComponents/investigation/InvestigationInputComponent'
import ObservationsInputComponent from '../inputComponents/observations/HealthObservationsInputComponent'
import PrescriptionDocumentsUploadSection from '../components/PrescriptionDocumentsUploadSection'
import {
  getSRInitialDataForSettingExistingSelectedComplaints,
  getSRInitialDataForSettingExistingSelectedDiagnosis,
  ScreeningReportBody,
  ScreeningReportBodyFirstSection,
  ScreeningReportHeader,
} from '../../ScreeningReportMakingPage/ScreeningReportMakingPageComponents'
import {
  PMPParent,
  PMPSectionHeading,
  PMPSectionHeadingTabView,
  SectionSecondaryHeadingAndContent,
} from '../PrescriptionMakingPageStyles'
import { CancelAndRedoComponent } from '../PrescriptionMakingPageComponents'
import { PRESCRIPTION_ITEM_ORDER_IDS } from '../printComponents/utils'

/**
 * Per-section toolbar for the tablet (DocOn) layout — shows the same Undo (clear-all) and
 * print-toggle affordances the cards carry in the other layouts. Pass `onCancel`/`onRedo` to
 * enable the Undo control; omit them for sections that only carry a print toggle (e.g. Rx).
 */
function DocOnSectionControls({
  sectionId,
  emrSectionsToShowInOrder,
  SectionPrintIndicator,
  showCancel,
  onCancel,
  onRedo,
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        marginBottom: '6px',
      }}
    >
      {onCancel && (
        <CancelAndRedoComponent
          showCancel={showCancel}
          cancelClickFunction={onCancel}
          redoClickFunction={onRedo}
        />
      )}
      {SectionPrintIndicator && (
        <SectionPrintIndicator
          sectionId={sectionId}
          emrSectionsToShowInOrder={emrSectionsToShowInOrder}
        />
      )}
    </div>
  )
}

/**
 * Renders a single EMR section for the Docon (multi-page / card-stack) layout.
 *
 * @param {string} sectionId - The section identifier
 * @param {number} idxInPage - Index of this section on the current Docon page (affects context menu side)
 * @param {object} deps - All closed-over state, setters, refs and helpers from PrescriptionMakingPage
 */
export function renderSectionForDocon(sectionId, idxInPage, deps) {
  const {
    emrSectionsToShowInOrder,
    screeningSections,
    screeningReportTemplateData,
    bookedEventInfo,
    mentorProfileData,
    setPatientHistoryNotes,
    patientHistoryNotesRef,
    auxiliaryDataFromProps,
    emrSectionsEditorDialogHandleOpen,
    emrSections,
    freeFormRichText,
    setFreeFormRichText,
    screeningReportData,
    screeningReportVisible,
    setScreeningReportVisible,
    setScreeningReportData,
    clinicId,
    clinicBrandId,
    selectedObservations,
    setSelectedObservations,
    prescriptionViewSettings,
    recommendedHealthEntityForDoctor,
    selectedComplaints,
    setSelectedComplaints,
    selectedDiagnosis,
    setSelectedDiagnosis,
    jointHomunculusData,
    setJointHomunculusData,
    drugAdviceTypes,
    drugAdviceDurations,
    drugAdviceFrequencies,
    drugAdviceNotes,
    drugAdviceWhatTimeOptions,
    drugAdviceDosages,
    drugsAdvice,
    handleAddDrugAdviceItem,
    handleDrugAdviceChange,
    handleDeleteDrugAdviceItem,
    setDrugsAdvice,
    selectedInvestigations,
    setSelectedInvestigations,
    openInvestigationForm,
    showInvestigationForm,
    closeInvestigationForm,
    submitInvestigation,
    initialInvestigationObjectToEdit,
    investigationToEdit,
    setInvestigationToEdit,
    setSavingInvestigation,
    handleChangeInvestigationToEdit,
    investigationTitleValid,
    setInvestigationTitleValid,
    categoriesListFromStore,
    sampleTypes,
    savingInvestigation,
    confirmationModalProps,
    selectedAdvice,
    setSelectedAdvice,
    selectedInstructions,
    setSelectedInstructions,
    eventId,
    patientId,
    prescriptionData,
    setPrescriptionData,
    emrSectionDataList,
    setEmrSectionDataList,
    keepSectionStateBeforeClearAll,
    undoClearAllChangeInPrescription,
    SectionPrintIndicator,
    nextVisitDetailsSection,
    renderPatientVitals,
    renderPrivateNotesSection,
    renderReferToSection,
    renderObservations,
    renderHealthComplaints,
    renderDiagnosis,
    renderJointHomunculus,
    renderDrugAdvice,
    renderInvestigations,
    renderAdvice,
    renderInstructions,
    renderPayemntAndRefundSection,
    renderTherapyRecommendationSection,
    renderPsychAssessmentSelection,
    renderSignatureSection,
    renderPrescriptionView,
  } = deps

  const contextMenuSide = idxInPage % 2 === 0 ? 'right' : 'left'
  // Used by the generic fallback + screening sections, mirroring renderSectionForHP.
  const currentSection = emrSectionsToShowInOrder?.find((item) => item?.id === sectionId)
  switch (sectionId) {
    case 'patient-history-notes':
      {
        const patientHistorySection = emrSectionsToShowInOrder?.find(
          (item) => item?.id === 'patient-history-notes',
        )
      return (
        <>
          <PatientHistoryNotesDisplay
            screeningSections={screeningSections}
            screeningReportTemplateData={screeningReportTemplateData}
            bookedEventInfo={bookedEventInfo}
            mentorProfileData={mentorProfileData}
            setPatientHistoryNotes={setPatientHistoryNotes}
            ref={patientHistoryNotesRef}
            isFirstScreening={
              auxiliaryDataFromProps?.patientVisitSummary
                .sessionsCompletedForCurrentEventTypeForCurrentMentor
            }
              onOpenEmrSectionsEditor={emrSectionsEditorDialogHandleOpen}
              isPrintableOnRx={patientHistorySection?.printable || false}
          />
        </>
      )
      }
    case 'free_form_rich_text': {
      const freeFormSectionForHeading = emrSections?.find((s) => s?.id === 'free_form_rich_text')
      const freeFormHeading = freeFormSectionForHeading?.title ?? freeFormSectionForHeading?.label ?? 'Free-form Rich Text'
      const isPrintableOnRx = freeFormSectionForHeading?.printable ?? false
      return (
        <Card
          sx={{
            mb: { xs: 2, sm: 3 },
            maxWidth: '1200px',
            width: '100%',
            background: 'linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)',
            border: '1px solid rgba(67, 56, 202, 0.12)',
            borderRadius: { xs: '12px', sm: '16px' },
            boxShadow: { xs: '0 2px 8px rgba(67, 56, 202, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)', sm: '0 4px 20px rgba(67, 56, 202, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)' },
            overflow: 'visible',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: { xs: '3px', sm: '4px' },
              background: 'linear-gradient(90deg, rgba(67, 56, 202, 0.8) 0%, rgba(99, 102, 241, 0.8) 100%)',
              borderRadius: { xs: '12px 12px 0 0', sm: '16px 16px 0 0' },
            },
          }}
          className={isPrintableOnRx ? '' : 'hide-on-print'}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                mb: { xs: 2, sm: 2.5 },
                pb: { xs: 1.5, sm: 2 },
                borderBottom: '2px solid rgba(67, 56, 202, 0.1)',
                gap: { xs: 1.5, sm: 0 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                <Box
                  sx={{
                    width: { xs: '36px', sm: '40px' },
                    height: { xs: '36px', sm: '40px' },
                    borderRadius: { xs: '8px', sm: '10px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                    color: '#4338ca',
                    flexShrink: 0,
                  }}
                >
                  <EditNote sx={{ fontSize: { xs: 20, sm: 22 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#1e293b',
                      fontSize: { xs: '18px', sm: '20px' },
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      letterSpacing: '-0.01em',
                      mb: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    {freeFormHeading}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontSize: { xs: '11px', sm: '12px' },
                      fontStyle: 'italic',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    Free-form notes for prescription
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' }, mt: { xs: 0.5, sm: 0 } }}>
                <SectionPrintIndicator sectionId="free_form_rich_text" emrSectionsToShowInOrder={emrSectionsToShowInOrder} />
              </Box>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <StyledRTEContainer style={{ position: 'relative' }}>
                <PatientHistoryTipTapEditor
                  value={freeFormRichText}
                  onChange={setFreeFormRichText}
                  placeholder="Write free-form notes..."
                  scrollableContentArea
                  maxHeightForScroll={380}
                  containerDivStyles={{
                    background: 'white',
                    padding: 0,
                    border: '1px solid rgba(67, 56, 202, 0.1)',
                    borderRadius: '12px',
                  }}
                />
              </StyledRTEContainer>
            </Box>
          </CardContent>
        </Card>
      )
    }
    case 'screeningReportData':
      return (
        <>
          {' '}
          {screeningReportData && (
            <>
              <PMPSectionHeading>
                Screening Report{' '}
                <span
                  style={{
                    textDecoration: 'none',
                    fontSize: '12px',
                    fontWeight: '400',
                    cursor: 'pointer',
                  }}
                  onClick={() => setScreeningReportVisible((prev) => !prev)}
                >
                  {screeningReportVisible ? 'Hide' : 'Show'}
                </span>
              </PMPSectionHeading>
              {screeningReportVisible && (
                <PMPParent>
                  <ScreeningReportHeader
                    screeningReportData={screeningReportData}
                    screenerName={screeningReportData?.mentorName}
                    templateData={screeningReportTemplateData}
                    timesVisit={bookedEventInfo?.patientMeetingSeqNumber}
                    doctorName={mentorProfileData?.name}
                  />
                  <div style={{ flex: 1, padding: '20px' }}>
                    <ScreeningReportBodyFirstSection
                      observationAndAdvice={{
                        advice: screeningReportData?.advice || null,
                        observation: screeningReportData?.observation || null,
                      }}
                      selectedComplaints={getSRInitialDataForSettingExistingSelectedComplaints(
                        screeningReportData?.complaints,
                      )}
                      selectedDiagnosis={getSRInitialDataForSettingExistingSelectedDiagnosis(
                        screeningReportData?.diagnosis,
                      )}
                      screeningSections={screeningSections}
                      screeningQuestionAnswers={screeningReportData?.screeningAnswers}
                      reportData={screeningReportData}
                    />
                    <ScreeningReportBody
                      observationAndAdvice={{
                        advice: screeningReportData?.advice || null,
                        observation: screeningReportData?.observation || null,
                      }}
                      content={screeningReportData?.content}
                      suggestedBookingTherapy={screeningReportData?.suggestedBookingTherapy}
                    />
                  </div>
                </PMPParent>
              )}
            </>
          )}
        </>
      )
    case 'observations':
      return (
        <>
          <DocOnSectionControls
            sectionId="observations"
            emrSectionsToShowInOrder={emrSectionsToShowInOrder}
            SectionPrintIndicator={SectionPrintIndicator}
            showCancel={Array.isArray(selectedObservations) && selectedObservations?.length > 0}
            onCancel={() => {
              keepSectionStateBeforeClearAll('observations', selectedObservations)
              setSelectedObservations([])
            }}
            onRedo={() =>
              undoClearAllChangeInPrescription('observations', (data) =>
                setSelectedObservations(data),
              )
            }
          />
          <ObservationsInputComponent
            clinicId={clinicId}
            clinicBrandId={clinicBrandId}
            observations={selectedObservations}
            setSelectedObservations={setSelectedObservations}
            prescriptionViewSettings={prescriptionViewSettings}
            recommendedObservations={recommendedHealthEntityForDoctor?.[HE_KEY_OBSERVATIONS]}
            contextMenuSide={contextMenuSide}
          />
        </>
      )
    case 'health_complaints':
      return (
        <>
          <DocOnSectionControls
            sectionId="health_complaints"
            emrSectionsToShowInOrder={emrSectionsToShowInOrder}
            SectionPrintIndicator={SectionPrintIndicator}
            showCancel={Array.isArray(selectedComplaints) && selectedComplaints?.length > 0}
            onCancel={() => {
              keepSectionStateBeforeClearAll('health_complaints', selectedComplaints)
              setSelectedComplaints([])
            }}
            onRedo={() =>
              undoClearAllChangeInPrescription('health_complaints', (data) =>
                setSelectedComplaints(data),
              )
            }
          />
          <HealthComplaintsInputComponent
            clinicId={clinicId}
            clinicBrandId={clinicBrandId}
            selectedComplaints={selectedComplaints}
            setSelectedComplaints={setSelectedComplaints}
            prescriptionViewSettings={prescriptionViewSettings}
            recommendedComplaints={recommendedHealthEntityForDoctor?.[HE_KEY_HEALTH_COMPLAINTS]}
            contextMenuSide={contextMenuSide}
          />
        </>
      )
    case 'diagnosis':
      // NOTE: no positioned/high-z wrapper here. In tablet view DiagnosisSelectMenu renders
      // pills (not the react-select dropdown), so a `position:relative; z-index:1000` wrapper
      // would only (a) steal the contextual drawer's containing block and (b) paint over it.
      return (
        <>
          <DocOnSectionControls
            sectionId="diagnosis"
            emrSectionsToShowInOrder={emrSectionsToShowInOrder}
            SectionPrintIndicator={SectionPrintIndicator}
            showCancel={Array.isArray(selectedDiagnosis) && selectedDiagnosis?.length > 0}
            onCancel={() => {
              keepSectionStateBeforeClearAll('diagnosis', selectedDiagnosis)
              setSelectedDiagnosis([])
            }}
            onRedo={() =>
              undoClearAllChangeInPrescription('diagnosis', (data) => setSelectedDiagnosis(data))
            }
          />
          <SectionSecondaryHeadingAndContent>
            <DiagnosisSelectMenu
              clinicId={clinicId}
              clinicBrandId={clinicBrandId}
              selectedDiagnosis={selectedDiagnosis}
              setSelectedDiagnosis={setSelectedDiagnosis}
              prescriptionViewSettings={prescriptionViewSettings}
              recommendedDiagnosis={recommendedHealthEntityForDoctor?.[HE_KEY_DIAGNOSIS]}
              contextMenuSide={contextMenuSide}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </SectionSecondaryHeadingAndContent>
        </>
      )
    case 'joint_homunculus':
      return (
        <>
          <PMPSectionHeadingTabView>
            <label
              style={{ textDecoration: 'inherit', cursor: 'pointer' }}
              htmlFor="join-homunculus-interface-checkbox"
            >
              44-Joint Homunculus
            </label>{' '}
          </PMPSectionHeadingTabView>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <JointHomunculusComponent
              editable={true}
              data={jointHomunculusData}
              setData={setJointHomunculusData}
            />
          </div>
        </>
      )

    case 'drug_advice': {
      const drugAdviceSubSections = emrSectionsToShowInOrder?.find(
        (item) => item?.id === 'drug_advice',
      )?.subSections
      return (
        <>
          <DocOnSectionControls
            sectionId="drug_advice"
            emrSectionsToShowInOrder={emrSectionsToShowInOrder}
            SectionPrintIndicator={SectionPrintIndicator}
          />
          <DrugAdviceInput
            clinicBrandId={clinicBrandId}
            clinicId={clinicId}
            drugAdviceTypes={drugAdviceTypes}
            drugAdviceDurations={drugAdviceDurations}
            drugAdviceFrequencies={drugAdviceFrequencies}
            drugAdviceNotes={drugAdviceNotes}
            drugAdviceWhatTimeOptions={drugAdviceWhatTimeOptions}
            drugAdviceDosages={drugAdviceDosages}
            drugsAdvice={drugsAdvice}
            handleAddDrugAdviceItem={handleAddDrugAdviceItem}
            handleDrugAdviceChange={handleDrugAdviceChange}
            handleDeleteDrugAdviceItem={handleDeleteDrugAdviceItem}
            mentorProfileData={mentorProfileData}
            prescriptionViewSettings={prescriptionViewSettings}
            recommendedHealthEntityForDoctor={recommendedHealthEntityForDoctor}
            setDrugsAdvice={setDrugsAdvice}
            contextMenuSide={contextMenuSide}
            drugAdviceSubSections={drugAdviceSubSections}
          />
        </>
      )
    }
    case 'investigations':
      return (
        <>
          <DocOnSectionControls
            sectionId="investigations"
            emrSectionsToShowInOrder={emrSectionsToShowInOrder}
            SectionPrintIndicator={SectionPrintIndicator}
            showCancel={Array.isArray(selectedInvestigations) && selectedInvestigations?.length > 0}
            onCancel={() => {
              keepSectionStateBeforeClearAll('investigations', selectedInvestigations)
              setSelectedInvestigations([])
            }}
            onRedo={() =>
              undoClearAllChangeInPrescription('investigations', (data) =>
                setSelectedInvestigations(data),
              )
            }
          />
          <SectionSecondaryHeadingAndContent>
            <InvestigationsSelectMenu
              clinicId={clinicId}
              clinicBrandId={clinicBrandId}
              selectedInvestigations={selectedInvestigations}
              setSelectedInvestigations={setSelectedInvestigations}
              onCreateInvestigation={openInvestigationForm}
              prescriptionViewSettings={prescriptionViewSettings}
              recommendedInvestigationIds={
                recommendedHealthEntityForDoctor?.[HE_KEY_INVESTIGATION]
              }
              contextMenuSide={contextMenuSide}
            />
          </SectionSecondaryHeadingAndContent>

          {/* <ButtonSCNContainer
                style={{ marginTop: '8px', fontSize: '12px' }}
                onClick={(e) => setNewTestAnchor(e.currentTarget)}
              >
                Add New
              </ButtonSCNContainer> */}
          <Dialog
            open={showInvestigationForm}
            onClose={closeInvestigationForm}
            PaperProps={{
              sx: { maxWidth: '768px !important', width: '100% important' },
            }}
          >
            <DialogTitle>Add Investigation</DialogTitle>
            <DialogContent>
              <form id="investigation-form" onSubmit={submitInvestigation}>
                <InvestigationForm
                  initialObject={initialInvestigationObjectToEdit}
                  investigation={investigationToEdit}
                  setInvestigation={setInvestigationToEdit}
                  setSaving={setSavingInvestigation}
                  handleChangeObject={handleChangeInvestigationToEdit}
                  titleValid={investigationTitleValid}
                  setTitleValid={setInvestigationTitleValid}
                  clinicId={clinicId}
                  clinicBrandId={clinicBrandId}
                  customOptions={{
                    categories:
                      convertAssessmentIdBasedCategoryListToMetaCategoryList(
                        categoriesListFromStore,
                      ),
                    investigationSample: sampleTypes || [],
                  }}
                />
              </form>
            </DialogContent>
            <DialogActions>
              <Button sx={{ color: 'grey' }} onClick={closeInvestigationForm}>
                Close
              </Button>
              <Button
                variant="contained"
                color="success"
                type="submit"
                form="investigation-form"
                disabled={savingInvestigation || !investigationTitleValid}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          <Suspense fallback={null}>
            <ConfirmationModal newImplementation {...confirmationModalProps} />
          </Suspense>

          {/* <ButtonSCNContainer
                style={{ marginTop: '8px', fontSize: '12px' }}
                onClick={(e) => setNewTestAnchor(e.currentTarget)}
              >
                Add New
              </ButtonSCNContainer> */}
        </>
      )
    case 'advice':
      return (
        <>
          <DocOnSectionControls
            sectionId="advice"
            emrSectionsToShowInOrder={emrSectionsToShowInOrder}
            SectionPrintIndicator={SectionPrintIndicator}
            showCancel={Array.isArray(selectedAdvice) && selectedAdvice?.length > 0}
            onCancel={() => {
              keepSectionStateBeforeClearAll('advice', selectedAdvice)
              setSelectedAdvice([])
            }}
            onRedo={() =>
              undoClearAllChangeInPrescription('advice', (data) => setSelectedAdvice(data))
            }
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <AdviceInputComponent
              clinicId={clinicId}
              clinicBrandId={clinicBrandId}
              advices={selectedAdvice}
              prescriptionViewSettings={prescriptionViewSettings}
              recommendedAdvices={recommendedHealthEntityForDoctor?.[HE_KEY_ADVICE]}
              setAdvices={setSelectedAdvice}
              contextMenuSide={contextMenuSide}
            />
          </div>
        </>
      )
    case 'instructions':
      return (
        <>
          <DocOnSectionControls
            sectionId="instructions"
            emrSectionsToShowInOrder={emrSectionsToShowInOrder}
            SectionPrintIndicator={SectionPrintIndicator}
            showCancel={Array.isArray(selectedInstructions) && selectedInstructions?.length > 0}
            onCancel={() => {
              keepSectionStateBeforeClearAll('instructions', selectedInstructions)
              setSelectedInstructions([])
            }}
            onRedo={() =>
              undoClearAllChangeInPrescription('instructions', (data) =>
                setSelectedInstructions(data),
              )
            }
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <EMRInstructionsInput
              clinicId={clinicId}
              clinicBrandId={clinicBrandId}
              selectedInstructions={selectedInstructions}
              setSelectedInstructions={setSelectedInstructions}
              prescriptionViewSettings={prescriptionViewSettings}
              recommendedInstructionIds={
                recommendedHealthEntityForDoctor?.[HE_KEY_INSTRUCTIONS]
              }
            />
          </div>
        </>
      )
    case 'prescription_images':
      {
        const section = emrSectionsToShowInOrder?.find((item) => item?.id === 'prescription_images')
        return (
          <PrescriptionDocumentsUploadSection
            eventId={eventId}
            bookedEventInfo={bookedEventInfo}
            prescriptionData={prescriptionData}
            setPrescriptionData={setPrescriptionData}
            mentorProfileData={mentorProfileData}
            emrSectionsToShowInOrder={emrSectionsToShowInOrder}
            section={section}
          />
        )
      }
    case 'finshing_details':
      return (
        <>
          {' '}
          {emrSections?.find((item) => item?.id === 'next_visit_details')?.selected && (
            <>{nextVisitDetailsSection(bookedEventInfo)}</>
          )}
          {emrSections?.find((item) => item?.id === 'therapy_recommendation')?.selected && (
            <>{renderTherapyRecommendationSection()}</>
          )}
          {emrSections?.find((item) => item?.id === 'payment_and_refund')?.selected && (
            <>{renderPayemntAndRefundSection()}</>
          )}
        </>
      )
    case 'prescription':
      return <>{renderPrescriptionView({ showInUI: true })}</>
    case 'patient-vitals':
      // Tablet view: let the Vitals card grow to fill its (tall) column rather than leaving
      // empty space below a short card.
      return (
        <Box
          sx={{
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            '& > .MuiCard-root': { minHeight: '100%', flex: 1, mb: 0 },
          }}
        >
          {renderPatientVitals()}
        </Box>
      )
    case 'private_notes':
      return renderPrivateNotesSection()
    case PRESCRIPTION_ITEM_ORDER_IDS.refer_to:
      return renderReferToSection()
    case 'signature':
      return renderSignatureSection()
    case 'psych_assessments':
      return renderPsychAssessmentSelection()
    case 'screening-recommendation': {
      const screeningMentorDataName = screeningReportData?.mentorName
      let sectionTitle = 'Your Recommendations'
      let showRedBorder = false
      if (
        screeningMentorDataName &&
        !auxiliaryDataFromProps?.emrSectionData?.some(
          (section) => section.sectionCode === 'SCREENING_RECOMMENDATION',
        )
      ) {
        showRedBorder = true
        sectionTitle = `Recommendation by ${screeningMentorDataName}`
      }
      return (
        <ScreeningRecommendationEmrSection
          emrSectionDataList={emrSectionDataList}
          setEmrSectionDataList={setEmrSectionDataList}
          screeningRecommendationEmrSection={currentSection}
          sectionTitle={sectionTitle}
          showRedBorder={showRedBorder}
        />
      )
    }
    case 'screening-report':
      return (
        <ScreeningReportViewer
          bookedEventInfo={bookedEventInfo}
          screeningReportData={screeningReportData}
          screeningAssessmentReports={auxiliaryDataFromProps?.screeningAssessmentReports ?? []}
          screeningReportTemplateData={screeningReportTemplateData}
          screeningEmrSectionDataList={auxiliaryDataFromProps?.['screening__EmrSectionData']}
          mentorProfileData={mentorProfileData}
          screeningSections={screeningSections}
          screeningReportVisible={screeningReportVisible}
          setScreeningReportVisible={setScreeningReportVisible}
          setScreeningReportData={setScreeningReportData}
        />
      )
    default:
      // Generic EMR section (custom/configurable sections) — mirrors renderSectionForHP so the
      // tablet layout never falls through to a blank "No Section rendered" card.
      return (
        <EmrSectionDataHandler
          emrSection={currentSection}
          emrSectionDataList={emrSectionDataList}
          practitionerRole="ROLE_DOCTOR"
          setEmrSectionDataList={setEmrSectionDataList}
          runtime={{
            patientId: patientId,
            eventId: eventId,
            clinicId: clinicId,
          }}
        />
      )
  }
}

/**
 * Renders a single EMR section for the HP (Health Professional / single-page) layout.
 *
 * @param {string} sectionId - The section identifier
 * @param {object} deps - All closed-over state, setters, refs and helpers from PrescriptionMakingPage
 */
export function renderSectionForHP(sectionId, deps) {
  const {
    emrSectionsToShowInOrder,
    screeningReportData,
    auxiliaryDataFromProps,
    emrSectionDataList,
    setEmrSectionDataList,
    patientHistoryNotesRef,
    setPatientHistoryNotes,
    bookedEventInfo,
    emrSectionsEditorDialogHandleOpen,
    patientHistoryEmrData,
    setPatientHistoryEmrData,
    emrSections,
    freeFormRichText,
    setFreeFormRichText,
    screeningReportVisible,
    setScreeningReportVisible,
    setScreeningReportData,
    mentorProfileData,
    screeningSections,
    screeningReportTemplateData,
    eventId,
    patientId,
    clinicId,
    prescriptionData,
    setPrescriptionData,
    SectionPrintIndicator,
    nextVisitDetailsSection,
    renderSignatureSection,
    renderPatientVitals,
    renderObservations,
    renderHealthComplaints,
    renderDiagnosis,
    renderJointHomunculus,
    renderDrugAdvice,
    renderInvestigations,
    renderAdvice,
    renderInstructions,
    renderPayemntAndRefundSection,
    renderPrivateNotesSection,
    renderReferToSection,
    renderTherapyRecommendationSection,
    renderPsychAssessmentSelection,
  } = deps

  const currentSection = emrSectionsToShowInOrder?.find((item) => item?.id === sectionId)
  switch (sectionId) {
    case 'screening-recommendation': {
      let screeningMentorDataName = screeningReportData?.mentorName
      let sectionTitle = 'Your Recommendations'
      let showRedBorder = false
      if (
        screeningMentorDataName &&
        !auxiliaryDataFromProps?.emrSectionData?.some(
          (section) => section.sectionCode === 'SCREENING_RECOMMENDATION',
        )
      ) {
        showRedBorder = true
        sectionTitle = `Recommendation by ${screeningMentorDataName}`
      }
      return (
        <>
          <ScreeningRecommendationEmrSection
            emrSectionDataList={emrSectionDataList}
            setEmrSectionDataList={setEmrSectionDataList}
            screeningRecommendationEmrSection={currentSection}
            sectionTitle={sectionTitle}
            showRedBorder={showRedBorder}
          />
        </>
      )
    }
    case 'patient-history-notes':
      {
        const patientHistorySection = emrSectionsToShowInOrder?.find(
          (item) => item?.id === 'patient-history-notes',
        )
      return (
        <>
          <PatientHistoryNotesDisplay
            bookedEventInfo={bookedEventInfo}
            screeningReportData={screeningReportData}
            screeningReportId={screeningReportData?.id}
            blockImporting={screeningReportData?.exportLog?.length > 0}
            setScreeningReportData={setScreeningReportData}
            setPatientHistoryNotes={setPatientHistoryNotes}
            ref={patientHistoryNotesRef}
            currentSectionData={currentSection}
            isFirstScreening={
              auxiliaryDataFromProps?.patientVisitSummary
                ?.sessionsCompletedForCurrentEventTypeForCurrentMentor <= 1
            }
            screeningEmrSectionDataList={auxiliaryDataFromProps?.['screening__EmrSectionData']}
            patientHistoryEmrData={patientHistoryEmrData}
            setPatientHistoryEmrData={setPatientHistoryEmrData}
            setEmrSectionDataList={setEmrSectionDataList}
            emrSectionDataList={emrSectionDataList}
              onOpenEmrSectionsEditor={emrSectionsEditorDialogHandleOpen}
              isPrintableOnRx={patientHistorySection?.printable || false}
          />
        </>
      )
      }
    case 'free_form_rich_text': {
      const freeFormSectionForHeading = emrSections?.find((s) => s?.id === 'free_form_rich_text')
      const freeFormHeading = freeFormSectionForHeading?.title ?? freeFormSectionForHeading?.label ?? 'Free-form Rich Text'
      const isPrintableOnRx = freeFormSectionForHeading?.printable ?? false
      return (
        <Card
          sx={{
            mb: { xs: 2, sm: 3 },
            maxWidth: '1200px',
            width: '100%',
            background: 'linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)',
            border: '1px solid rgba(67, 56, 202, 0.12)',
            borderRadius: { xs: '12px', sm: '16px' },
            boxShadow: { xs: '0 2px 8px rgba(67, 56, 202, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)', sm: '0 4px 20px rgba(67, 56, 202, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)' },
            overflow: 'visible',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: { xs: '3px', sm: '4px' },
              background: 'linear-gradient(90deg, rgba(67, 56, 202, 0.8) 0%, rgba(99, 102, 241, 0.8) 100%)',
              borderRadius: { xs: '12px 12px 0 0', sm: '16px 16px 0 0' },
            },
          }}
          className={isPrintableOnRx ? '' : 'hide-on-print'}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                mb: { xs: 2, sm: 2.5 },
                pb: { xs: 1.5, sm: 2 },
                borderBottom: '2px solid rgba(67, 56, 202, 0.1)',
                gap: { xs: 1.5, sm: 0 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                <Box
                  sx={{
                    width: { xs: '36px', sm: '40px' },
                    height: { xs: '36px', sm: '40px' },
                    borderRadius: { xs: '8px', sm: '10px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                    color: '#4338ca',
                    flexShrink: 0,
                  }}
                >
                  <EditNote sx={{ fontSize: { xs: 20, sm: 22 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#1e293b',
                      fontSize: { xs: '18px', sm: '20px' },
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      letterSpacing: '-0.01em',
                      mb: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    {freeFormHeading}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontSize: { xs: '11px', sm: '12px' },
                      fontStyle: 'italic',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    Free-form notes for prescription
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' }, mt: { xs: 0.5, sm: 0 } }}>
                <SectionPrintIndicator sectionId="free_form_rich_text" emrSectionsToShowInOrder={emrSectionsToShowInOrder} />
              </Box>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <StyledRTEContainer style={{ position: 'relative' }}>
                <PatientHistoryTipTapEditor
                  value={freeFormRichText}
                  onChange={setFreeFormRichText}
                  placeholder="Write free-form notes..."
                  scrollableContentArea
                  maxHeightForScroll={380}
                  containerDivStyles={{
                    background: 'white',
                    padding: 0,
                    border: '1px solid rgba(67, 56, 202, 0.1)',
                    borderRadius: '12px',
                  }}
                />
              </StyledRTEContainer>
            </Box>
          </CardContent>
        </Card>
      )
    }
    case 'screening-report':
      return (
        <ScreeningReportViewer
          bookedEventInfo={bookedEventInfo}
          screeningReportData={screeningReportData}
          screeningAssessmentReports={auxiliaryDataFromProps?.screeningAssessmentReports ?? []}
          screeningReportTemplateData={screeningReportTemplateData}
          screeningEmrSectionDataList={auxiliaryDataFromProps?.['screening__EmrSectionData']}
          mentorProfileData={mentorProfileData}
          screeningSections={screeningSections}
          screeningReportVisible={screeningReportVisible}
          setScreeningReportVisible={setScreeningReportVisible}
          setScreeningReportData={setScreeningReportData}
        />
      )
    case 'patient-vitals':
      return renderPatientVitals()
    case 'private_notes':
      return renderPrivateNotesSection()
    case PRESCRIPTION_ITEM_ORDER_IDS.refer_to:
      return renderReferToSection()
    case 'observations':
      return renderObservations()
    case 'health_complaints':
      return renderHealthComplaints()
    case 'diagnosis':
      return renderDiagnosis()
    case 'joint_homunculus':
      return renderJointHomunculus()
    case 'drug_advice':
      return renderDrugAdvice()
    case 'investigations':
      return renderInvestigations()
    case 'advice':
      return renderAdvice()
    case 'next_visit_details':
      return nextVisitDetailsSection(bookedEventInfo)
    case 'therapy_recommendation':
      return renderTherapyRecommendationSection()
    case 'payment_and_refund':
      return renderPayemntAndRefundSection()
    case 'instructions':
      return renderInstructions()
    case 'psych_assessments':
      return renderPsychAssessmentSelection()
    case 'prescription_images':
      return (
        <PrescriptionDocumentsUploadSection
          eventId={eventId}
          bookedEventInfo={bookedEventInfo}
          prescriptionData={prescriptionData}
          setPrescriptionData={setPrescriptionData}
          mentorProfileData={mentorProfileData}
        />
      )
    case 'signature':
      return renderSignatureSection()
    default:
      return (
        <EmrSectionDataHandler
          emrSection={currentSection}
          emrSectionDataList={emrSectionDataList}
          practitionerRole="ROLE_DOCTOR"
          setEmrSectionDataList={setEmrSectionDataList}
          runtime={{
            patientId: patientId,
            eventId: eventId,
            clinicId: clinicId,
          }}
        />
      )
  }
}
